/**
 * EG Search (Meilisearch) – autocomplete controller.
 *
 * Handles the input events + dropdown behavior (open/close, scroll lock, keyboard nav,
 * and click-outside).
 */
window.EGSearchAutocomplete = (function () {
  'use strict';

  const render = window.EGSearchAutocompleteRender;
  const api = window.EGSearchAutocompleteApi;

  // Keyboard navigation
  function navigateItems(items, activeItem, direction) {
    if (!items.length) return;

    const itemsArray = Array.from(items);
    let currentIndex = activeItem ? itemsArray.indexOf(activeItem) : -1;

    if (activeItem) {
      activeItem.setAttribute('aria-selected', 'false');
    }

    if (direction === 'next') {
      currentIndex = currentIndex < itemsArray.length - 1 ? currentIndex + 1 : 0;
    } else {
      currentIndex = currentIndex > 0 ? currentIndex - 1 : itemsArray.length - 1;
    }

    itemsArray[currentIndex].setAttribute('aria-selected', 'true');
  }

  // Init
  function init() {
    const input = document.getElementById('autocomplete-0-input') ||
      document.getElementById('search-input') ||
      document.querySelector('input[type="search"]');
    const dropdown = document.getElementById('autocomplete-dropdown');

    if (!input) return;

    const form = input.closest('form');
    const clearBtn = form ? form.querySelector('.aa-ClearButton') : null;

    function syncClearButton() {
      if (!clearBtn) return;
      clearBtn.hidden = input.value.trim().length === 0;
    }

    // Keep the dropdown inside `.header-wrapper` so it stays anchored to the header.
    let autocompleteContainer = dropdown;

    // If it already exists, make sure it's a direct child of the header wrapper.
    if (autocompleteContainer) {
      const headerWrapper =
        document.querySelector('sticky-header.header-wrapper') ||
        document.querySelector('.header-wrapper');

      if (headerWrapper && autocompleteContainer.parentElement !== headerWrapper) {
        // Move it under the header wrapper (positioning works better there).
        headerWrapper.appendChild(autocompleteContainer);
      }
    } else {
      // Create the dropdown container if it doesn't exist yet.
      const headerWrapper =
        document.querySelector('sticky-header.header-wrapper') ||
        document.querySelector('.header-wrapper');

      autocompleteContainer = document.createElement('div');
      autocompleteContainer.id = 'autocomplete-dropdown';
      autocompleteContainer.className = 'aa-Panel aa-Panel--autocomplete';

      if (headerWrapper) {
        headerWrapper.appendChild(autocompleteContainer);
      } else {
        document.body.appendChild(autocompleteContainer);
      }
    }

    let debounceTimer;
    let requestVersion = 0;

    function setScrollLock(locked) {
      const html = document.documentElement;
      const body = document.body;
      if (!html || !body) return;

      if (locked) {
        html.classList.add('eg-autocomplete-open');
        body.classList.add('eg-autocomplete-open');
      } else {
        html.classList.remove('eg-autocomplete-open');
        body.classList.remove('eg-autocomplete-open');
      }
    }

    // On mobile, pin the dropdown right under the search bar.
    function positionDropdownBelowSearchBar() {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      if (!isMobile) return;

      const searchBar = document.getElementById('autocomplete') ||
        document.querySelector('.header-search');

      if (searchBar && autocompleteContainer) {
        const rect = searchBar.getBoundingClientRect();
        const topPosition = rect.bottom;
        autocompleteContainer.style.setProperty('top', `${topPosition}px`, 'important');
        autocompleteContainer.style.setProperty('max-height', `calc(100vh - ${topPosition}px)`, 'important');
      }
    }

    function openDropdown() {
      autocompleteContainer.classList.add('open');
      // Also toggle `.open` on the form wrapper (desktop sizing/spacing).
      const autocompleteForm = document.querySelector('.aa-Autocomplete');
      if (autocompleteForm) autocompleteForm.classList.add('open');
      positionDropdownBelowSearchBar();
      setScrollLock(true);
    }

    function closeDropdown() {
      clearTimeout(debounceTimer);
      requestVersion += 1;
      autocompleteContainer.classList.remove('open');
      // Clear inline display so CSS can take over again.
      autocompleteContainer.style.display = '';
      // Also remove `.open` from the form wrapper.
      const autocompleteForm = document.querySelector('.aa-Autocomplete');
      if (autocompleteForm) autocompleteForm.classList.remove('open');
      setScrollLock(false);
    }

    input.closest('.header-search')?.addEventListener('eg:search-close', closeDropdown);

    // Clear button behavior
    if (clearBtn) {
      // Ensure the initial state is correct (e.g. bfcache restores).
      syncClearButton();

      clearBtn.addEventListener('click', (e) => {
        // Don't let the document click handler treat this as an "outside click".
        e.preventDefault();
        e.stopPropagation();

        clearTimeout(debounceTimer);
        requestVersion += 1;
        input.value = '';
        syncClearButton();

        // Show the default panel immediately after clearing.
        render.renderDefaultPanel(autocompleteContainer);
        openDropdown();
        input.focus();
      });
    }

    // Show a panel on focus (default or query results).
    input.addEventListener('focus', async () => {
      clearTimeout(debounceTimer);
      const version = ++requestVersion;
      syncClearButton();
      const query = input.value.trim();
      if (query.length < 1) {
        render.renderDefaultPanel(autocompleteContainer);
        openDropdown();
      } else {
        try {
          const { suggestionsData, productsData, articlesData } = await api.fetchResults(query);
          if (version !== requestVersion) return;
          const searchId = String(productsData && productsData.search_id ? productsData.search_id : (suggestionsData && suggestionsData.search_id ? suggestionsData.search_id : ''));
          const hits = (productsData.hits || []).map((p) => ({ ...p, __search_id: searchId }));
          render.renderSearchPanel(query, suggestionsData.suggestions || [], hits, productsData.nbHits || 0, articlesData.hits || [], articlesData.nbHits || 0, autocompleteContainer);
          openDropdown();
        } catch (err) {
          console.error('Focus search error:', err);
        }
      }
    });

    // Handle typing (debounced).
    input.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const version = ++requestVersion;
      const query = e.target.value.trim();
      syncClearButton();

      if (query.length < 1) {
        render.renderDefaultPanel(autocompleteContainer);
        openDropdown();
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          const { suggestionsData, productsData, articlesData } = await api.fetchResults(query);
          if (version !== requestVersion) return;
          const searchId = String(productsData && productsData.search_id ? productsData.search_id : (suggestionsData && suggestionsData.search_id ? suggestionsData.search_id : ''));
          const hits = (productsData.hits || []).map((p) => ({ ...p, __search_id: searchId }));
          render.renderSearchPanel(query, suggestionsData.suggestions || [], hits, productsData.nbHits || 0, articlesData.hits || [], articlesData.nbHits || 0, autocompleteContainer);
          openDropdown();
        } catch (err) {
          if (version !== requestVersion) return;
          console.error('Autocomplete error:', err);
          closeDropdown();
        }
      }, 150);
    });

    // Close when clicking outside the search UI.
    document.addEventListener('click', (e) => {
      const dropdownEl = document.getElementById('autocomplete-dropdown');

      // Use the dropdown's `.open` class as the source of truth.
      if (!dropdownEl || !dropdownEl.classList.contains('open')) return;

      const searchContainer = document.getElementById('autocomplete');
      const autocompleteForm = document.querySelector('.aa-Autocomplete');
      const headerSearch = document.querySelector('.header-search');

      // If the click is inside any of the search-related containers, don't close.
      if (searchContainer && searchContainer.contains(e.target)) return;
      if (autocompleteForm && autocompleteForm.contains(e.target)) return;
      if (headerSearch && headerSearch.contains(e.target)) return;
      if (dropdownEl && dropdownEl.contains(e.target)) return;

      // Click was outside: close the dropdown.
      closeDropdown();
    });

    // Keyboard navigation
    input.addEventListener('keydown', (e) => {
      const items = autocompleteContainer.querySelectorAll('.aa-PanelSection--left .aa-Item');
      const activeItem = autocompleteContainer.querySelector('.aa-PanelSection--left .aa-Item[aria-selected="true"]');

      if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateItems(items, activeItem, 'next');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateItems(items, activeItem, 'prev');
      } else if (e.key === 'Enter') {
        if (activeItem) {
          e.preventDefault();
          const link = activeItem.querySelector('a');
          if (link) link.click();
        }
      }
    });
  }

  return {
    init,
    // Back-compat: expose render helpers on the controller.
    renderDefaultPanel: function (container) { return render.renderDefaultPanel(container); },
    renderSearchPanel: function (...args) { return render.renderSearchPanel(...args); },
    renderProductCard: function (...args) { return render.renderProductCard(...args); },
    renderArticleCard: function (...args) { return render.renderArticleCard(...args); }
  };
})();
